import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import useTitle from "../hooks/useTitle";

export default function AddBookingPage() {
  useTitle("Bookings | Astro Portal");
  // console.log(sessionStorage.getItem("authSession"));
  const navigate = useNavigate();

  const authSession = sessionStorage.getItem("authSession");
  const authData = authSession ? JSON.parse(authSession) : null;
  const token = authData?.jwt_token || null;
  const loggedInUserId = authData?.user?.id || null;
  const role = authData?.role || "user"; // user | admin | astrologer

  const [form, setForm] = useState({
    user_id: role === "user" ? loggedInUserId : "",
    astrologer_id: role === "astrologer" ? loggedInUserId : "",
    date: "",
    amount: 0,
    discount: 0,
    total_amount: 0,
    coupon_code: "",
    slots: [],
    payment_method: "",
  });

  const [users, setUsers] = useState([]);
  const [astrologers, setAstrologers] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [ratePerMinute, setRatePerMinute] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [gateways, setGateways] = useState([]);

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token]);

  // Fetch based on role
  useEffect(() => {
    if (role === "astrologer") {
      axios.get(`${API_BASE_URL}/users`).then((res) => {
        setUsers(res.data?.data || []);
      });
    }

    if (role === "user") {
      axios.get(`${API_BASE_URL}/astrologers`).then((res) => {
        setAstrologers(res.data?.data || []);
      });
    }
  }, [role]);

  // Fetch gateways
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => res.data.success && setGateways(res.data.data));
  }, []);

  // Fetch slots when astrologer/date selected
  useEffect(() => {
    if (form.astrologer_id && form.date) fetchSlots();
  }, [form.astrologer_id, form.date]);

  const fetchSlots = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/bookings/available-slots`, {
        params: { astrologer_id: form.astrologer_id, date: form.date },
      });

      const data = res.data;
      setRatePerMinute(parseFloat(data.rate_per_minute || 0));
      generateTimeSlots(data);
    } catch {
      setToast({ message: "Failed to fetch slots.", type: "error" });
    }
  };

  const generateTimeSlots = (data) => {
    const booked = data.booked_slots.map((slot) => ({
      start: parseTime(slot.start_time),
      end: parseTime(slot.end_time),
    }));

    const slots = [];
    for (
      let t = parseTime(data.working_hours.start);
      t < parseTime(data.working_hours.end);
      t.setMinutes(t.getMinutes() + 30)
    ) {
      const next = new Date(t.getTime() + 30 * 60000);

      const isBooked = booked.some(
        (b) => t < b.end && next > b.start
      );

      slots.push({
        label: `${formatTime(t)} - ${formatTime(next)}`,
        start: formatTime(t),
        end: formatTime(next),
        disabled: isBooked,
      });
    }

    setTimeSlots(slots);
    setSelectedSlots([]);
  };

  const parseTime = (time) => {
    const [h, m] = time.split(":").map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
  };

  const formatTime = (d) =>
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });

  const handleSlotSelect = (slot) => {
    const exists = selectedSlots.some((s) => s.start === slot.start);
    const updated = exists
      ? selectedSlots.filter((s) => s.start !== slot.start)
      : [...selectedSlots, { start: slot.start, end: slot.end }];

    setSelectedSlots(updated);
    updateAmount(updated);
  };

  const updateAmount = (slots) => {
    if (!slots.length)
      return setForm((p) => ({
        ...p,
        amount: 0,
        total_amount: 0,
        slots: [],
        discount: 0,
      }));

    const minutes = slots.length * 30;
    const amount = minutes * ratePerMinute;

    setForm((prev) => ({
      ...prev,
      amount,
      total_amount: amount - prev.discount,
      slots,
    }));
  };

  const applyCoupon = async () => {
    if (!form.coupon_code) return setToast({ message: "Enter coupon", type: "error" });

    try {
      const res = await axios.post(
        `${API_BASE_URL}/apply-coupon`,
        { coupon_code: form.coupon_code, amount: form.amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setForm((prev) => ({
        ...prev,
        discount: res.data.discount || 0,
        total_amount: prev.amount - (res.data.discount || 0),
      }));

      setToast({ message: "Coupon Applied!", type: "success" });
    } catch {
      setToast({ message: "Invalid Coupon", type: "error" });
    }
  };


  const handleSubmit = async () => {
    try {
      setLoading(true);

      await axios.post(`${API_BASE_URL}/bookings`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setToast({ message: "Booking Created!", type: "success" });

      setForm({
        user_id: role === "user" ? loggedInUserId : "",
        astrologer_id: role === "astrologer" ? loggedInUserId : "",
        date: "",
        amount: 0,
        payment_method: "",
        discount: 0,
        total_amount: 0,
        coupon_code: "",
        slots: [],
      });
      setSelectedSlots([]);
    } catch {
      setToast({ message: "Failed!", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <div className="container my-5" style={{ maxWidth: 900 }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-4 shadow-sm bg-light">
        <h3 className="fw-bold mb-4">📅 Add Booking</h3>

        {/* USER FIELD (Admin or Astrologer only) */}
        {(role === "astrologer") && (
          <div className="mb-3">
            <label className="form-label">User</label>
            <select name="user_id" value={form.user_id} onChange={handleChange} className="form-select">
              <option value="">Select User</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name}({u.email})</option>
              ))}
            </select>
          </div>
        )}

        {/* ASTROLOGER FIELD (Admin or User only) */}
        {(role === "user") && (
          <div className="mb-3">
            <label className="form-label">Astrologer</label>
            <select
              name="astrologer_id"
              value={form.astrologer_id}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">Select Astrologer</option>
              {astrologers.map((a) => (
                <option key={a.id} value={a.id}>{a.first_name} {a.last_name}({a.email})</option>
              ))}
            </select>
          </div>
        )}

        {/* Date */}
        <div className="mb-3">
          <label className="form-label">Date</label>
          <input type="date" name="date" value={form.date} onChange={handleChange} className="form-control" />
        </div>

        {/* Time Slots */}
        {timeSlots.length > 0 && (
          <div className="mb-3">
            <label className="form-label">Available Time Slots</label>
            <div className="d-flex flex-wrap gap-2">
              {timeSlots.map((slot) => (
                <button
                  key={slot.label}
                  type="button"
                  disabled={slot.disabled}
                  className={`btn btn-sm ${
                    selectedSlots.find((s) => s.start === slot.start)
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => handleSlotSelect(slot)}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Amount Section */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Amount</label>
            <input type="number" value={form.amount} disabled className="form-control" />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Coupon Code</label>
            <div className="input-group">
              <input type="text" name="coupon_code" value={form.coupon_code} onChange={handleChange} className="form-control" />
              <button className="btn btn-warning" onClick={applyCoupon}>Apply</button>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Discount</label>
            <input type="number" disabled value={form.discount} className="form-control" />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Final Amount</label>
            <input type="number" disabled value={form.total_amount} className="form-control" />
          </div>
        </div>

        {/* Payment Method */}
        <div className="mb-3">
          <label className="form-label">Payment Method</label>
          <select name="payment_method" value={form.payment_method} onChange={handleChange} className="form-select">
            <option value="">Select Payment Method</option>
            {gateways.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>

        <div className="text-end">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loading}
            onClick={handleSubmit}
            className="btn btn-success rounded-pill px-4"
          >
            {loading ? "Creating..." : "Create Booking"}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
